import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import NursingHomeDashboard from "./NursingHomeDashboard";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import TenantBillingActions from "../components/TenantBillingActions";
import SuperAdminNav from "../components/SuperAdminNav";
import {
  TENANT_STANDALONE_ACCESS,
  TENANT_GROUP_ACCESS,
  buildFullTenantAccessMap,
  applyTenantAccessDefaults,
  getPageAccessLabel,
} from "../access/pageAccess";

export default function TenantDetail({ user }) {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const isSuperAdminRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin/tenant/");
  const [tenant, setTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [memberBusyId, setMemberBusyId] = useState("");
  const [memberRoleDrafts, setMemberRoleDrafts] = useState({});
  const [inviteBusyId, setInviteBusyId] = useState("");
  const [inviteFilter, setInviteFilter] = useState("pending");
  const [inviteSearch, setInviteSearch] = useState("");
  const [invitePage, setInvitePage] = useState(1);
  const [memberFilter, setMemberFilter] = useState("all");
  const [memberSearch, setMemberSearch] = useState("");
  const [memberPage, setMemberPage] = useState(1);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");
  const [logs, setLogs] = useState([]);
  const [logSearch, setLogSearch] = useState("");
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logPageSize, setLogPageSize] = useState(10);
  const [logsLoading, setLogsLoading] = useState(false);
  const [tenantSettings, setTenantSettings] = useState({
    name: "",
    slug: "",
    contactEmail: "",
    allowedDomainsText: "",
    defaultInviteRole: "employee",
    seatLimit: "",
    brandingEnabled: false,
    sidebarBrandingEnabled: false,
    brandName: "",
    publicBrandName: "",
    logoUrl: "",
    logoPath: "",
    primaryColor: "#2a0516",
    secondaryColor: "#645155",
    accentColor: "#f15822",
    tenantPageAccess: buildFullTenantAccessMap()
  });
  const [brandingBusy, setBrandingBusy] = useState(false);
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const tenantRef = doc(db, "tenants", tenantId);
        const tenantSnap = await getDoc(tenantRef);

        if (!tenantSnap.exists()) {
          if (!ignore) {
            setTenant(null);
            setMembers([]);
            setErr("Tenant not found.");
          }
          return;
        }

        const tenantData = { id: tenantSnap.id, ...tenantSnap.data() };
        const membersSnap = await getDocs(collection(db, "tenants", tenantId, "members"));
        const memberRows = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const invitesSnap = await getDocs(
          query(collection(db, "tenantInvites"), where("tenantId", "==", tenantId))
        );
        const inviteRows = invitesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const ownerMemberEmail =
          memberRows.find((m) => (m.role || "").toLowerCase() === "owner" && m.email)?.email || "";

        if (!ignore) {
          setTenant(tenantData);
          setTenantSettings({
            name: tenantData.name || "",
            slug: tenantData.slug || "",
            contactEmail: tenantData.contactEmail || tenantData.ownerEmail || ownerMemberEmail || "",
            allowedDomainsText: Array.isArray(tenantData.allowedDomains) ? tenantData.allowedDomains.join(", ") : "",
            defaultInviteRole: tenantData.defaultInviteRole || "employee",
            seatLimit: tenantData.seatLimit == null ? "" : String(tenantData.seatLimit),
            brandingEnabled: tenantData.branding?.enabled === true,
            sidebarBrandingEnabled: tenantData.branding?.sidebarEnabled === true,
            brandName: tenantData.branding?.brandName || tenantData.name || "",
            publicBrandName: tenantData.branding?.publicBrandName || tenantData.name || "",
            logoUrl: tenantData.branding?.logoUrl || "",
            logoPath: tenantData.branding?.logoPath || "",
            primaryColor: tenantData.branding?.colors?.primary || "#2a0516",
            secondaryColor: tenantData.branding?.colors?.secondary || "#645155",
            accentColor: tenantData.branding?.colors?.accent || "#f15822",
            tenantPageAccess: applyTenantAccessDefaults(tenantData.tenantPageAccess || {}),
          });
          setMembers(memberRows);
          setMemberRoleDrafts(Object.fromEntries(memberRows.map((m) => [m.id, m.role || "employee"])));
          setInvites(inviteRows);
        }
      } catch (e) {
        if (!ignore) {
          setErr(e?.message || "Failed to load tenant.");
          setTenant(null);
          setMembers([]);
          setInvites([]);
          setLogs([]);
          setLogTotal(0);
          setLogTotalPages(1);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let ignore = false;

    async function loadLogs() {
      setLogsLoading(true);

      try {
        let logRows = [];
        let logTotalRows = 0;
        let logTotalPagesValue = 1;

        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const logParams = new URLSearchParams({
            tenantId,
            q: logSearch,
            page: String(logPage),
            pageSize: String(logPageSize),
          });

          const auditUrls = [
            `https://us-central1-lastwishbox-business.cloudfunctions.net/getTenantAuditLogs?${logParams.toString()}`,
            `/api/getTenantAuditLogs?${logParams.toString()}`
          ];

          for (const url of auditUrls) {
            try {
              const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const data = await res.json().catch(() => ({}));
              if (res.ok && Array.isArray(data.logs)) {
                logRows = data.logs;
                logTotalRows = Number(data.total || 0);
                logTotalPagesValue = Math.max(1, Number(data.totalPages || 1));
                break;
              }
            } catch {}
          }
        }

        if (!ignore) {
          setLogs(logRows);
          setLogTotal(logTotalRows);
          setLogTotalPages(logTotalPagesValue);
        }
      } catch {
        if (!ignore) {
          setLogs([]);
          setLogTotal(0);
          setLogTotalPages(1);
        }
      } finally {
        if (!ignore) setLogsLoading(false);
      }
    }

    loadLogs();
    return () => {
      ignore = true;
    };
  }, [tenantId, logSearch, logPage, logPageSize]);

  async function submitInvite(e) {
    e.preventDefault();
    setInviteMsg("");

    const email = String(inviteEmail || "").trim().toLowerCase();
    if (!email) {
      setInviteMsg("Please enter an email address.");
      return;
    }

    try {
      setInviteBusy(true);

      const user = auth.currentUser;
      if (!user) {
        setInviteMsg("You must be signed in.");
        return;
      }

      const token = await user.getIdToken();

      const res = await fetch("/api/createTenantInvite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          tenantId,
          email,
          role: inviteRole
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to send invite");
      }

      setInviteEmail("");
      setInviteRole("employee");
      setInviteMsg(`Invite sent to ${email}`);

      try {
        const invitesSnap = await getDocs(
          query(collection(db, "tenantInvites"), where("tenantId", "==", tenantId))
        );
        const inviteRows = invitesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setInvites(inviteRows);
      } catch {}
    } catch (e) {
      setInviteMsg(e?.message || "Failed to send invite");
    } finally {
      setInviteBusy(false);
    }
  }

  const nowMs = Date.now();
  const pendingInvites = invites.filter((i) => i.status === "pending" && (!i.expiresAtMs || i.expiresAtMs > nowMs));
  const acceptedInvites = invites.filter((i) => i.status === "accepted");
  const expiredInvites = invites.filter((i) => i.status === "pending" && i.expiresAtMs && i.expiresAtMs <= nowMs);

  function fmtDate(ms) {
    if (!ms) return "—";
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return "—";
    }
  }


  async function setMemberStatus(memberUid, action) {
    try {
      setMemberBusyId(memberUid);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/setTenantMemberStatus",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            tenantId,
            memberUid,
            action
          })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Update failed");

      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberUid
            ? { ...m, status: action === "suspend" ? "suspended" : "active" }
            : m
        )
      );
    } catch (e) {
      alert(e.message || "Failed to update member");
    } finally {
      setMemberBusyId("");
    }
  }



  // eslint-disable-next-line no-unused-vars
  async function setMemberRole(memberUid) {
    try {
      setMemberBusyId(memberUid);

      const role = memberRoleDrafts[memberUid] || "employee";
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/setTenantMemberRole",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            tenantId,
            memberUid,
            role
          })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Role update failed");

      setMembers((prev) =>
        prev.map((m) => (m.id === memberUid ? { ...m, role } : m))
      );
    } catch (e) {
      alert(e.message || "Failed to update member role");
    } finally {
      setMemberBusyId("");
    }
  }


  async function removeMember(memberUid) {
    const ok = window.confirm("Remove this member from the tenant?");
    if (!ok) return;

    try {
      setMemberBusyId(memberUid);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/removeTenantMember",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            tenantId,
            memberUid
          })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Remove failed");

      setMembers((prev) => prev.filter((m) => m.id !== memberUid));
    } catch (e) {
      alert(e.message || "Failed to remove member");
    } finally {
      setMemberBusyId("");
    }
  }



  async function revokeInvite(inviteId) {
    const ok = window.confirm("Revoke this invitation?");
    if (!ok) return;

    try {
      setInviteBusyId(inviteId);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/revokeTenantInvite",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ inviteId })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Revoke failed");

      setInvites((prev) =>
        prev.map((inv) =>
          inv.id === inviteId
            ? { ...inv, status: "revoked", revokedAtMs: Date.now() }
            : inv
        )
      );
    } catch (e) {
      alert(e.message || "Failed to revoke invite");
    } finally {
      setInviteBusyId("");
    }
  }



  const invitePageSize = 10;

  function getInviteStatus(inv) {
    if (inv.status === "accepted") return "accepted";
    if (inv.status === "revoked") return "revoked";
    if (inv.status === "pending" && inv.expiresAtMs && inv.expiresAtMs <= nowMs) return "expired";
    return inv.status || "pending";
  }

  const suggestedAllowedDomain = (() => {
    const email = String(tenantSettings.contactEmail || "").trim().toLowerCase();
    const domain = email.includes("@") ? email.split("@").pop() : "";
    const freeDomains = new Set([
      "gmail.com",
      "googlemail.com",
      "outlook.com",
      "hotmail.com",
      "live.com",
      "msn.com",
      "yahoo.com",
      "icloud.com",
      "me.com",
      "aol.com",
      "proton.me",
      "protonmail.com"
    ]);

    if (!domain || freeDomains.has(domain)) return "lastwishbox.com";
    return domain;
  })();

  const filteredInvites = invites
    .slice()
    .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))
    .filter((inv) => {
      const status = getInviteStatus(inv);
      const matchesFilter = inviteFilter === "all" ? true : status === inviteFilter;
      const q = inviteSearch.trim().toLowerCase();
      const matchesSearch = !q ? true : String(inv.email || "").toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });

  const invitePageCount = Math.max(1, Math.ceil(filteredInvites.length / invitePageSize));
  const safeInvitePage = Math.min(invitePage, invitePageCount);
  const pagedInvites = filteredInvites.slice(
    (safeInvitePage - 1) * invitePageSize,
    safeInvitePage * invitePageSize
  );


  const memberPageSize = 10;

  function getMemberStatus(member) {
    return member.status || "active";
  }

  const filteredMembers = members
    .slice()
    .sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")))
    .filter((member) => {
      const status = getMemberStatus(member);
      const matchesFilter = memberFilter === "all" ? true : status === memberFilter;
      const q = memberSearch.trim().toLowerCase();
      const matchesSearch = !q ? true : String(member.email || "").toLowerCase().includes(q);
      return matchesFilter && matchesSearch;
    });

  const memberPageCount = Math.max(1, Math.ceil(filteredMembers.length / memberPageSize));
  const safeMemberPage = Math.min(memberPage, memberPageCount);
  const pagedMembers = filteredMembers.slice(
    (safeMemberPage - 1) * memberPageSize,
    safeMemberPage * memberPageSize
  );


  async function resendInvite(inviteId) {
    try {
      setInviteBusyId(inviteId);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/resendTenantInvite",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ inviteId })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Resend failed");

      setInvites((prev) =>
        prev.map((inv) =>
          inv.id === inviteId
            ? { ...inv, resentAtMs: Date.now() }
            : inv
        )
      );
    } catch (e) {
      alert(e.message || "Failed to resend invite");
    } finally {
      setInviteBusyId("");
    }
  }



  function setTenantPageAccessKey(key, enabled) {
    setTenantSettings((prev) => ({
      ...prev,
      tenantPageAccess: {
        ...applyTenantAccessDefaults(prev.tenantPageAccess || {}),
        [key]: enabled === true,
      },
    }));
  }

  function setTenantGroupAccess(groupKey, childKeys, enabled) {
    setTenantSettings((prev) => {
      const nextAccess = applyTenantAccessDefaults(prev.tenantPageAccess || {});
      nextAccess[groupKey] = enabled === true;
      for (const childKey of childKeys) {
        nextAccess[childKey] = enabled === true;
      }
      return {
        ...prev,
        tenantPageAccess: nextAccess,
      };
    });
  }

  async function saveTenantSettings(e) {
    e.preventDefault();

    try {
      setSettingsBusy(true);
      setSettingsMsg("");

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const allowedDomains = String(tenantSettings.allowedDomainsText || "")
        .split(",")
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean);

      const branding = {
        enabled: tenantSettings.brandingEnabled === true,
        sidebarEnabled: tenantSettings.sidebarBrandingEnabled === true,
        brandName: String(tenantSettings.brandName || "").trim(),
        publicBrandName: String(tenantSettings.publicBrandName || "").trim(),
        logoUrl: String(tenantSettings.logoUrl || "").trim(),
        logoPath: String(tenantSettings.logoPath || "").trim(),
        colors: {
          primary: String(tenantSettings.primaryColor || "#2a0516").trim() || "#2a0516",
          secondary: String(tenantSettings.secondaryColor || "#645155").trim() || "#645155",
          accent: String(tenantSettings.accentColor || "#f15822").trim() || "#f15822"
        }
      };

      const tenantPageAccess = applyTenantAccessDefaults(tenantSettings.tenantPageAccess || {});

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/updateTenantSettings",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            tenantId,
            name: tenantSettings.name,
            slug: tenantSettings.slug,
            contactEmail: tenantSettings.contactEmail,
            allowedDomains,
            defaultInviteRole: tenantSettings.defaultInviteRole,
            seatLimit: tenantSettings.seatLimit === "" ? null : Number(tenantSettings.seatLimit),
            branding,
            tenantPageAccess
          })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to save tenant settings");

      setTenant((prev) => ({
        ...prev,
        name: tenantSettings.name,
        slug: tenantSettings.slug,
        contactEmail: tenantSettings.contactEmail,
        allowedDomains,
        defaultInviteRole: tenantSettings.defaultInviteRole,
        seatLimit: tenantSettings.seatLimit === "" ? null : Number(tenantSettings.seatLimit),
        tenantPageAccess,
        branding: {
          enabled: tenantSettings.brandingEnabled === true,
          sidebarEnabled: tenantSettings.sidebarBrandingEnabled === true,
          brandName: String(tenantSettings.brandName || "").trim(),
          publicBrandName: String(tenantSettings.publicBrandName || "").trim(),
          logoUrl: String(tenantSettings.logoUrl || "").trim(),
          logoPath: String(tenantSettings.logoPath || "").trim(),
          colors: {
            primary: String(tenantSettings.primaryColor || "#2a0516").trim() || "#2a0516",
            secondary: String(tenantSettings.secondaryColor || "#645155").trim() || "#645155",
            accent: String(tenantSettings.accentColor || "#f15822").trim() || "#f15822"
          }
        }
      }));

      setSettingsMsg("Tenant settings saved.");
    } catch (e) {
      setSettingsMsg(e?.message || "Failed to save tenant settings");
    } finally {
      setSettingsBusy(false);
    }
  }

  async function handleBrandLogoUpload(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;

    try {
      setBrandingBusy(true);
      setSettingsMsg("");

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const safeExt = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
      const objectPath = `tenants/${tenantId}/branding/logo.${safeExt || "png"}`;
      const objectRef = ref(storage, objectPath);

      await uploadBytes(objectRef, file, {
        contentType: file.type || "application/octet-stream"
      });

      const downloadUrl = await getDownloadURL(objectRef);

      setTenantSettings((prev) => ({
        ...prev,
        logoUrl: downloadUrl,
        logoPath: objectPath
      }));

      setSettingsMsg("Logo uploaded. Save settings to apply branding.");
    } catch (err) {
      setSettingsMsg(err?.message || "Failed to upload logo");
    } finally {
      setBrandingBusy(false);
      if (e?.target) e.target.value = "";
    }
  }



  const activeMemberCount = members.filter((m) => (m.status || "active") === "active").length;
  const seatLimitValue =
    tenantSettings.seatLimit === "" || tenantSettings.seatLimit == null
      ? null
      : Number(tenantSettings.seatLimit);
  const remainingSeats =
    seatLimitValue == null || !Number.isFinite(seatLimitValue)
      ? null
      : Math.max(seatLimitValue - activeMemberCount, 0);


  const formatTenantDate = (value) => {
    if (!value) return "";

    try {
      if (typeof value?.toDate === "function") {
        return value.toDate().toLocaleDateString();
      }

      if (typeof value?.seconds === "number") {
        return new Date(value.seconds * 1000).toLocaleDateString();
      }

      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      if (typeof value === "number") {
        return new Date(value).toLocaleDateString();
      }

      if (typeof value === "string") {
        return value;
      }
    } catch (e) {}

    return "";
  };

  const billingStatusValue = tenant?.billingStatus || "pending_checkout";
  const renewalValue = formatTenantDate(tenant?.renewalDate);
  const planValue = tenant?.planKey || "custom";
  const isFamily = tenant?.planKey === "family";
  const isNursingHome = tenant?.planKey === "nursinghome";
  const pendingPlanValue = tenant?.pendingPlanKey || "";


  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "24px" }}>
      {isSuperAdminRoute && <SuperAdminNav title={isNursingHome ? "Nursing Home" : isFamily ? "Family Dashboard" : "Tenant Detail"} active="tenants" />}
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
          {(!isNursingHome || !isSuperAdminRoute) && (
          <div>
            <h1 style={{ margin: 0, color: "#2a0516" }}>{isFamily ? "Family Dashboard" : isNursingHome ? "Nursing Home Dashboard" : "Tenant Detail"}</h1>
            <p style={{ margin: "6px 0 0", color: "#657899" }}>
              {isFamily ? "Manage your family plan and members." : isNursingHome ? "Resident wishes management and compliance overview." : "View tenant settings and membership."}
            </p>
          </div>
          )}

          {isSuperAdminRoute ? null : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {!isNursingHome && (
              <Link
                to={`/tenant/${tenantId}/support`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #d6dbe7",
                  background: "#fff",
                  color: "#2a0516",
                  textDecoration: "none",
                  fontWeight: 700
                }}
              >
                Support Dashboard
              </Link>
              )}
              {!isNursingHome && (
              <Link
                to={`/tenant/${tenantId}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #d6dbe7",
                  background: "#fff",
                  color: "#2a0516",
                  textDecoration: "none",
                  fontWeight: 700
                }}
              >
                Admin Dashboard
              </Link>
              )}
              {isNursingHome && (
                <Link
                  to="/support"
                  className="nh-support-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 42,
                    padding: "0 14px",
                    borderRadius: 10,
                    border: "1px solid #d6dbe7",
                    background: "#fff",
                    color: "#2a0516",
                    textDecoration: "none",
                    fontWeight: 700
                  }}
                >
                  Support
                </Link>
              )}
              <Link
                to="/profile"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #d6dbe7",
                  background: "#fff",
                  color: "#2a0516",
                  textDecoration: "none",
                  fontWeight: 700
                }}
              >
                Profile
              </Link>
            </div>
          )}
        </div>

        {err ? (
          <div style={{
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            borderRadius: 12,
            padding: 14,
            marginBottom: 16
          }}>
            {err}
          </div>
        ) : null}

        {loading ? (
          <div style={{
            background: "#fff",
            border: "1px solid #e8ebf3",
            borderRadius: 16,
            padding: 20
          }}>
            {isFamily ? "Loading family dashboard…" : isNursingHome ? "Loading nursing home dashboard…" : "Loading tenant…"}
          </div>
        ) : tenant ? (
          <>
            {isNursingHome ? (
              <NursingHomeDashboard tenantId={tenantId} tenant={tenant} />
            ) : (
            <>
            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>
                {tenant.name || tenant.id}
              </h2>

              <div style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                gap: "10px 16px"
              }}>
                <strong>Slug</strong><span>{tenant.slug || "—"}</span>
                <strong>Status</strong><span>{tenant.status || "—"}</span>
                <strong>Document ID</strong><span style={{ wordBreak: "break-word" }}>{tenant.id}</span>
              </div>
            </div>

            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>Billing Summary</h2>

              <div style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                gap: "10px 16px"
              }}>
                <strong>Plan</strong><span style={{ textTransform: "capitalize" }}>{planValue}</span>
                <strong>Billing Status</strong>
                <span style={{
                  textTransform: "capitalize",
                  color:
                    billingStatusValue === "active" ? "#166534" :
                    billingStatusValue === "pending_checkout" || billingStatusValue === "pending" ? "#92400e" :
                    billingStatusValue === "overdue" || billingStatusValue === "payment_issue" ? "#b91c1c" :
                    billingStatusValue === "cancelled" || billingStatusValue === "inactive" ? "#6b7280" :
                    "#111827",
                  fontWeight: 700
                }}>
                  {billingStatusValue}
                </span>
                <strong>Renewal Date</strong><span>{renewalValue || "—"}</span>
                <strong>Seat Limit</strong><span>{tenant?.seatLimit == null || tenant?.seatLimit === "" ? "—" : tenant.seatLimit}</span>
                {pendingPlanValue && pendingPlanValue !== planValue ? (
                  <>
                    <strong>Scheduled Plan Change</strong><span style={{ textTransform: "capitalize" }}>{pendingPlanValue}</span>
                    <strong>Effective On</strong><span>{renewalValue || "Next renewal"}</span>
                  </>
                ) : null}
              </div>

              <TenantBillingActions tenantId={tenantId} currentPlan={planValue} hasStripeSubscription={!!tenant?.stripeSubscriptionId} />

            </div>

            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>{isFamily ? "Family Settings" : "Tenant Settings"}</h2>

              <form onSubmit={saveTenantSettings} style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                      {isFamily ? "Family name" : "Company name"}
                    </label>
                    <input
                      type="text"
                      value={tenantSettings.name}
                      onChange={(e) => setTenantSettings((prev) => ({ ...prev, name: e.target.value }))}
                      style={{
                        width: "100%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                      Slug
                    </label>
                    <input
                      type="text"
                      value={tenantSettings.slug}
                      onChange={(e) => setTenantSettings((prev) => ({ ...prev, slug: e.target.value }))}
                      style={{
                        width: "100%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                      Contact email
                    </label>
                    <input
                      type="email"
                      value={tenantSettings.contactEmail}
                      onChange={(e) => setTenantSettings((prev) => ({ ...prev, contactEmail: e.target.value }))}
                      style={{
                        width: "100%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ display: isFamily ? "none" : undefined }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                      Default invite role
                    </label>
                    <select
                      value={tenantSettings.defaultInviteRole}
                      onChange={(e) => setTenantSettings((prev) => ({ ...prev, defaultInviteRole: e.target.value }))}
                      style={{
                        width: "100%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14,
                        background: "#fff"
                      }}
                    >
                      <option value="owner">owner</option>
                      <option value="hr_admin">hr_admin</option>
                      <option value="employee">employee</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                      Seat limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={tenantSettings.seatLimit}
                      onChange={(e) => isSuperAdminRoute && setTenantSettings((prev) => ({ ...prev, seatLimit: e.target.value }))}
                      disabled={!isSuperAdminRoute}
                      style={{
                        width: "100%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14,
                        background: isSuperAdminRoute ? "#fff" : "#f6f7fb",
                        cursor: isSuperAdminRoute ? "text" : "not-allowed",
                        color: isSuperAdminRoute ? "#1a1a2e" : "#657899",
                      }}
                    />
                    {!isSuperAdminRoute && (
                      <div style={{ fontSize: 12, color: "#657899", marginTop: 4 }}>
                        Seat limit is managed by LastWish Box. Contact support to change.
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: isFamily ? "none" : undefined }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                    Allowed domains
                  </label>
                  <input
                    type="text"
                    value={tenantSettings.allowedDomainsText}
                    onChange={(e) => setTenantSettings((prev) => ({ ...prev, allowedDomainsText: e.target.value }))}
                    placeholder={`${suggestedAllowedDomain}, anotherdomain.com`}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 10,
                      border: "1px solid #d6dbe7",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                  />
                  <div style={{ marginTop: 6, fontSize: 12, color: "#657899", lineHeight: 1.5 }}>
                    Use comma-separated company domains only. Example: <strong>{suggestedAllowedDomain}</strong>.
                  </div>
                </div>


                {isSuperAdminRoute ? (
                  <div style={{
                    marginTop: 8,
                    padding: 16,
                    borderRadius: 14,
                    border: "1px solid #e8ebf3",
                    background: "#fafbff"
                  }}>
                    <h3 style={{ marginTop: 0, marginBottom: 8, color: "#2a0516" }}>Portal Navigation</h3>
                    <div style={{ marginBottom: 14, fontSize: 13, color: "#657899", lineHeight: 1.5 }}>
                      All sidebar sections are enabled by default. You can disable specific sections for this tenant here.
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
                      {TENANT_STANDALONE_ACCESS.map((item) => (
                        <label
                          key={item.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minHeight: 40,
                            padding: "0 12px",
                            borderRadius: 10,
                            border: "1px solid #d6dbe7",
                            background: "#fff",
                            fontSize: 14,
                            color: "#2a0516",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={tenantSettings.tenantPageAccess?.[item.key] !== false}
                            onChange={(e) => setTenantPageAccessKey(item.key, e.target.checked)}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>

                    <div style={{ display: "grid", gap: 12 }}>
                      {TENANT_GROUP_ACCESS.map((group) => {
                        const groupEnabled = tenantSettings.tenantPageAccess?.[group.key] !== false;
                        return (
                          <div
                            key={group.key}
                            style={{
                              border: "1px solid #d6dbe7",
                              borderRadius: 12,
                              background: "#fff",
                              padding: 12
                            }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 10,
                                fontSize: 14,
                                color: "#2a0516",
                                fontWeight: 800,
                                cursor: "pointer"
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={groupEnabled}
                                onChange={(e) => setTenantGroupAccess(group.key, group.children, e.target.checked)}
                              />
                              {group.label}
                            </label>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                gap: 10,
                                opacity: groupEnabled ? 1 : 0.6
                              }}
                            >
                              {group.children.map((childKey) => (
                                <label
                                  key={childKey}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    minHeight: 38,
                                    padding: "0 10px",
                                    borderRadius: 10,
                                    border: "1px solid #e5e7eb",
                                    background: "#fff",
                                    fontSize: 13,
                                    color: "#2a0516",
                                    cursor: groupEnabled ? "pointer" : "not-allowed"
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={groupEnabled && tenantSettings.tenantPageAccess?.[childKey] !== false}
                                    onChange={(e) => setTenantPageAccessKey(childKey, e.target.checked)}
                                    disabled={!groupEnabled}
                                  />
                                  {getPageAccessLabel(childKey)}
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div style={{
                  marginTop: 8,
                  padding: 16,
                  borderRadius: 14,
                  border: "1px solid #e8ebf3",
                  background: "#fafbff",
                  display: isFamily ? "none" : undefined
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: 14, color: "#2a0516" }}>Branding</h3>

                  <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 14, flexWrap: "wrap" }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#657899" }}>
                      <input
                        type="checkbox"
                        checked={tenantSettings.brandingEnabled}
                        onChange={(e) => setTenantSettings((prev) => ({ ...prev, brandingEnabled: e.target.checked }))}
                        style={{ marginRight: 8 }}
                      />
                      Enable tenant branding
                    </label>

                    <label style={{ fontSize: 13, fontWeight: 700, color: "#657899" }}>
                      <input
                        type="checkbox"
                        checked={tenantSettings.sidebarBrandingEnabled}
                        onChange={(e) => setTenantSettings((prev) => ({ ...prev, sidebarBrandingEnabled: e.target.checked }))}
                        style={{ marginRight: 8 }}
                        disabled={!tenantSettings.brandingEnabled}
                      />
                      Apply branding to sidebar
                    </label>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Brand name
                      </label>
                      <input
                        type="text"
                        value={tenantSettings.brandName}
                        onChange={(e) => setTenantSettings((prev) => ({ ...prev, brandName: e.target.value }))}
                        placeholder="Build & Renovate"
                        style={{
                          width: "100%",
                          height: 40,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Public brand name
                      </label>
                      <input
                        type="text"
                        value={tenantSettings.publicBrandName}
                        onChange={(e) => setTenantSettings((prev) => ({ ...prev, publicBrandName: e.target.value }))}
                        placeholder="Build & Renovate Support"
                        style={{
                          width: "100%",
                          height: 40,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "0 12px",
                          fontSize: 14
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginBottom: 14, alignItems: "end" }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Logo upload
                      </label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleBrandLogoUpload}
                        disabled={brandingBusy}
                        style={{
                          width: "100%",
                          minHeight: 40,
                          borderRadius: 10,
                          border: "1px solid #d6dbe7",
                          padding: "8px 12px",
                          fontSize: 14,
                          background: "#fff"
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => setTenantSettings((prev) => ({ ...prev, logoUrl: "", logoPath: "" }))}
                      style={{
                        height: 40,
                        padding: "0 14px",
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        background: "#fff",
                        color: "#2a0516",
                        fontWeight: 700,
                        cursor: "pointer"
                      }}
                    >
                      Remove Logo
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Primary colour
                      </label>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
                        <input
                          type="color"
                          value={tenantSettings.primaryColor}
                          onChange={(e) => setTenantSettings((prev) => ({ ...prev, primaryColor: e.target.value }))}
                          style={{ width: "100%", height: 40, borderRadius: 10, border: "1px solid #d6dbe7", background: "#fff" }}
                        />
                        <button
                          type="button"
                          onClick={() => setTenantSettings((prev) => ({ ...prev, primaryColor: "#2a0516" }))}
                          style={{
                            height: 40,
                            padding: "0 12px",
                            borderRadius: 10,
                            border: "1px solid #d6dbe7",
                            background: "#fff",
                            color: "#2a0516",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Secondary colour
                      </label>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
                        <input
                          type="color"
                          value={tenantSettings.secondaryColor}
                          onChange={(e) => setTenantSettings((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                          style={{ width: "100%", height: 40, borderRadius: 10, border: "1px solid #d6dbe7", background: "#fff" }}
                        />
                        <button
                          type="button"
                          onClick={() => setTenantSettings((prev) => ({ ...prev, secondaryColor: "#645155" }))}
                          style={{
                            height: 40,
                            padding: "0 12px",
                            borderRadius: 10,
                            border: "1px solid #d6dbe7",
                            background: "#fff",
                            color: "#2a0516",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                        Accent colour
                      </label>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
                        <input
                          type="color"
                          value={tenantSettings.accentColor}
                          onChange={(e) => setTenantSettings((prev) => ({ ...prev, accentColor: e.target.value }))}
                          style={{ width: "100%", height: 40, borderRadius: 10, border: "1px solid #d6dbe7", background: "#fff" }}
                        />
                        <button
                          type="button"
                          onClick={() => setTenantSettings((prev) => ({ ...prev, accentColor: "#f15822" }))}
                          style={{
                            height: 40,
                            padding: "0 12px",
                            borderRadius: 10,
                            border: "1px solid #d6dbe7",
                            background: "#fff",
                            color: "#2a0516",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#fff"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "14px 16px",
                      background: tenantSettings.brandingEnabled ? tenantSettings.primaryColor : "#2a0516",
                      color: "#fff"
                    }}>
                      {tenantSettings.brandingEnabled && tenantSettings.logoUrl ? (
                        <img
                          src={tenantSettings.logoUrl}
                          alt="Brand logo preview"
                          style={{ height: 34, width: "auto", maxWidth: 120, objectFit: "contain", display: "block" }}
                        />
                      ) : (
                        <div style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: "#fff",
                          color: tenantSettings.brandingEnabled ? tenantSettings.primaryColor : "#2a0516",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: 14
                        }}>
                          LWB
                        </div>
                      )}
                      <div style={{ fontWeight: 800, fontSize: 16 }}>
                        {tenantSettings.brandingEnabled
                          ? (tenantSettings.publicBrandName || tenantSettings.brandName || tenantSettings.name || "Tenant Portal")
                          : "LastWishBox"}
                      </div>
                    </div>

                    <div style={{ padding: 16, background: tenantSettings.brandingEnabled ? "#ffffff" : "#ffffff" }}>
                      <div style={{ fontSize: 13, color: "#657899", marginBottom: 8 }}>Preview</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 8 }}>
                        {tenantSettings.brandingEnabled
                          ? (tenantSettings.brandName || tenantSettings.name || "Your Brand")
                          : "LastWishBox"}
                      </div>
                      <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 14 }}>
                        This preview shows how the tenant brand can appear in the portal header and actions.
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={{
                            height: 38,
                            padding: "0 14px",
                            borderRadius: 10,
                            border: "1px solid transparent",
                            background: tenantSettings.brandingEnabled ? tenantSettings.accentColor : "#003353",
                            color: "#fff",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Primary Action
                        </button>
                        <button
                          type="button"
                          style={{
                            height: 38,
                            padding: "0 14px",
                            borderRadius: 10,
                            border: `1px solid ${tenantSettings.brandingEnabled ? tenantSettings.secondaryColor : "#645155"}`,
                            background: "#fff",
                            color: tenantSettings.brandingEnabled ? tenantSettings.secondaryColor : "#645155",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Secondary Action
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="submit"
                    disabled={settingsBusy}
                    style={{
                      height: 40,
                      padding: "0 14px",
                      borderRadius: 10,
                      border: "1px solid #2a0516",
                      background: "#2a0516",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    {settingsBusy ? "Saving..." : "Save Settings"}
                  </button>

                  <Link
                    to={`/tenant/${tenantId}/support`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 40,
                      padding: "0 14px",
                      borderRadius: 10,
                      border: "1px solid #003353",
                      background: "#fff",
                      color: "#003353",
                      fontWeight: 700,
                      textDecoration: "none"
                    }}
                  >
                    Support Tickets
                  </Link>

                  {settingsMsg ? (
                    <div style={{ color: settingsMsg.toLowerCase().includes("saved") ? "#166534" : "#b91c1c" }}>
                      {settingsMsg}
                    </div>
                  ) : null}
                </div>
              </form>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  {isFamily ? "Family Members Allowed" : "Seat Limit"}
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#2a0516" }}>
                  {seatLimitValue == null || !Number.isFinite(seatLimitValue) ? "—" : seatLimitValue}
                </div>
              </div>

              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  Active Members
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#2a0516" }}>
                  {activeMemberCount}
                </div>
              </div>

              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  {isFamily ? "Remaining Invites" : "Remaining Seats"}
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: remainingSeats === 0 ? "#b91c1c" : "#2a0516" }}>
                  {remainingSeats == null ? "—" : remainingSeats}
                </div>
              </div>
            </div>

            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>{isFamily ? "Invite Family Member" : "Invite Member"}</h2>

              {remainingSeats === 0 ? (
                <div style={{
                  marginBottom: 14,
                  background: "#fff1f2",
                  border: "1px solid #fecdd3",
                  color: "#9f1239",
                  borderRadius: 12,
                  padding: 14
                }}>
                  {isFamily ? "All 5 family member seats are taken. Contact support to make changes." : "Seat limit reached. Increase the seat limit in Tenant Settings before sending more invites."}
                </div>
              ) : null}

              <form onSubmit={submitInvite} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, alignItems: "end" }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={isFamily ? "family member's email" : "person@company.com"}
                    style={{
                      width: "100%",
                      height: 44,
                      borderRadius: 10,
                      border: "1px solid #d6dbe7",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                  />
                </div>

                <div style={{ display: isFamily ? "none" : undefined }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#657899" }}>
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    style={{
                      width: "100%",
                      height: 44,
                      borderRadius: 10,
                      border: "1px solid #d6dbe7",
                      padding: "0 12px",
                      fontSize: 14,
                      background: "#fff"
                    }}
                  >
                    <option value="owner">owner</option>
                    <option value="hr_admin">hr_admin</option>
                    <option value="employee">employee</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={inviteBusy || remainingSeats === 0}
                  style={{
                    height: 44,
                    borderRadius: 10,
                    border: "1px solid #2a0516",
                    background: "#2a0516",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: inviteBusy || remainingSeats === 0 ? "default" : "pointer",
                    opacity: inviteBusy || remainingSeats === 0 ? 0.6 : 1
                  }}
                >
                  {inviteBusy ? "Sending…" : "Send Invite"}
                </button>
              </form>

              {inviteMsg ? (
                <div style={{ marginTop: 12, color: inviteMsg.toLowerCase().includes("sent") ? "#166534" : "#b91c1c" }}>
                  {inviteMsg}
                </div>
              ) : null}
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  Pending Invitations
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#2a0516" }}>
                  {pendingInvites.length}
                </div>
              </div>

              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  Accepted Invitations
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#2a0516" }}>
                  {acceptedInvites.length}
                </div>
              </div>

              <div style={{
                background: "#fff",
                border: "1px solid #e8ebf3",
                borderRadius: 16,
                padding: 18,
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
              }}>
                <div style={{ fontSize: 13, color: "#657899", fontWeight: 700, marginBottom: 8 }}>
                  Expired Invitations
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: "#2a0516" }}>
                  {expiredInvites.length}
                </div>
              </div>
            </div>

            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>Invitations</h2>

              {invites.length === 0 ? (
                <div style={{ color: "#657899" }}>No invitations found.</div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                    {["pending", "accepted", "revoked", "expired", "all"].map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => {
                          setInviteFilter(f);
                          setInvitePage(1);
                        }}
                        style={{
                          height: 34,
                          padding: "0 12px",
                          borderRadius: 999,
                          border: "1px solid " + (inviteFilter === f ? "#2a0516" : "#d6dbe7"),
                          background: inviteFilter === f ? "#2a0516" : "#fff",
                          color: inviteFilter === f ? "#fff" : "#2a0516",
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "capitalize"
                        }}
                      >
                        {f}
                      </button>
                    ))}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <input
                      type="text"
                      value={inviteSearch}
                      onChange={(e) => {
                        setInviteSearch(e.target.value);
                        setInvitePage(1);
                      }}
                      placeholder="Search invitations by email"
                      style={{
                        width: "100%",
                        maxWidth: 360,
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={th}>Email</th>
                          <th style={th}>Role</th>
                          <th style={th}>Status</th>
                          <th style={th}>Created</th>
                          <th style={th}>Expires</th>
                          <th style={th}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedInvites.length === 0 ? (
                          <tr>
                            <td style={td} colSpan={6}>No invitations match this view.</td>
                          </tr>
                        ) : (
                          pagedInvites.map((inv) => {
                            const derivedStatus = getInviteStatus(inv);

                            return (
                              <tr key={inv.id}>
                                <td style={td}>{inv.email || "—"}</td>
                                <td style={td}>{inv.role || "—"}</td>
                                <td style={td}>{derivedStatus}</td>
                                <td style={td}>{fmtDate(inv.createdAtMs)}</td>
                                <td style={td}>{fmtDate(inv.expiresAtMs)}</td>
                                <td style={td}>
                                  {derivedStatus === "pending" ? (
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                      <button
                                        type="button"
                                        onClick={() => resendInvite(inv.id)}
                                        disabled={inviteBusyId === inv.id}
                                        style={{
                                          height: 34,
                                          padding: "0 10px",
                                          borderRadius: 6,
                                          border: "1px solid #2a0516",
                                          background: "#fff",
                                          color: "#2a0516",
                                          fontWeight: 600,
                                          cursor: "pointer"
                                        }}
                                      >
                                        {inviteBusyId === inv.id ? "Saving..." : "Resend"}
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() => revokeInvite(inv.id)}
                                        disabled={inviteBusyId === inv.id}
                                        style={{
                                          height: 34,
                                          padding: "0 10px",
                                          borderRadius: 6,
                                          border: "1px solid #b91c1c",
                                          background: "#fff",
                                          color: "#b91c1c",
                                          fontWeight: 600,
                                          cursor: "pointer"
                                        }}
                                      >
                                        {inviteBusyId === inv.id ? "Saving..." : "Revoke"}
                                      </button>
                                    </div>
                                  ) : (
                                    <span style={{ color: "#9ca3af" }}>—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
                    <div style={{ color: "#657899", fontSize: 14 }}>
                      Showing {pagedInvites.length} of {filteredInvites.length}
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => setInvitePage((p) => Math.max(1, p - 1))}
                        disabled={safeInvitePage <= 1}
                        style={{
                          height: 34,
                          padding: "0 10px",
                          borderRadius: 6,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          color: "#2a0516",
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Prev
                      </button>

                      <span style={{ color: "#657899", fontSize: 14 }}>
                        Page {safeInvitePage} of {invitePageCount}
                      </span>

                      <button
                        type="button"
                        onClick={() => setInvitePage((p) => Math.min(invitePageCount, p + 1))}
                        disabled={safeInvitePage >= invitePageCount}
                        style={{
                          height: 34,
                          padding: "0 10px",
                          borderRadius: 6,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          color: "#2a0516",
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            

<div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
              marginBottom: 16
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>Recent Activity</h2>

              <div style={{ marginBottom: 14, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  type="text"
                  value={logSearch}
                  onChange={(e) => {
                    setLogSearch(e.target.value);
                    setLogPage(1);
                  }}
                  placeholder="Search activity by action, actor, target or details"
                  style={{
                    width: "100%",
                    maxWidth: 420,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #d6dbe7",
                    padding: "0 12px",
                    fontSize: 14
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 12, color: "#657899" }}>
                    Show
                  </label>
                  <select
                    value={logPageSize}
                    onChange={(e) => {
                      setLogPageSize(Number(e.target.value));
                      setLogPage(1);
                    }}
                    style={{
                      height: 36,
                      borderRadius: 8,
                      border: "1px solid #d6dbe7",
                      padding: "0 10px",
                      fontSize: 13,
                      background: "#fff"
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <div style={{ fontSize: 12, color: "#657899" }}>
                    {logTotal} total{logsLoading ? " • Loading..." : ""}
                  </div>
                </div>
              </div>

              {logsLoading && logs.length === 0 ? (
                <div style={{ color: "#657899" }}>Loading activity...</div>
              ) : logs.length === 0 ? (
                <div style={{ color: "#657899" }}>No activity yet.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Action</th>
                        <th style={th}>Actor</th>
                        <th style={th}>Target</th>
                        <th style={th}>When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td style={td}>{log.action || "—"}</td>
                          <td style={td}>{log.actorEmail || log.actorUid || "—"}</td>
                          <td style={td}>{log.targetEmail || log.targetUid || "—"}</td>
                          <td style={td}>{fmtDate(log.createdAtMs)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 12, color: "#657899" }}>
                      Page {logPage} of {logTotalPages}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                        disabled={logPage <= 1}
                        style={{
                          height: 34,
                          padding: "0 12px",
                          borderRadius: 8,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          cursor: logPage <= 1 ? "not-allowed" : "pointer"
                        }}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}
                        disabled={logPage >= logTotalPages}
                        style={{
                          height: 34,
                          padding: "0 12px",
                          borderRadius: 8,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          cursor: logPage >= logTotalPages ? "not-allowed" : "pointer"
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </div>

            <div style={{
              background: "#fff",
              border: "1px solid #e8ebf3",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 8px 24px rgba(0,0,0,0.04)"
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: "#2a0516" }}>{isFamily ? "Family Members" : "Members"}</h2>

              {members.length === 0 ? (
                <div style={{ color: "#657899" }}>No members found.</div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                    {["all", "active", "suspended"].map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => {
                          setMemberFilter(f);
                          setMemberPage(1);
                        }}
                        style={{
                          height: 34,
                          padding: "0 12px",
                          borderRadius: 999,
                          border: "1px solid " + (memberFilter === f ? "#2a0516" : "#d6dbe7"),
                          background: memberFilter === f ? "#2a0516" : "#fff",
                          color: memberFilter === f ? "#fff" : "#2a0516",
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "capitalize"
                        }}
                      >
                        {f}
                      </button>
                    ))}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => {
                        setMemberSearch(e.target.value);
                        setMemberPage(1);
                      }}
                      placeholder="Search members by email"
                      style={{
                        width: "100%",
                        maxWidth: 360,
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #d6dbe7",
                        padding: "0 12px",
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={th}>Email</th>
                          <th style={th}>Status</th>
                          <th style={th}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedMembers.length === 0 ? (
                          <tr>
                            <td style={td} colSpan={5}>No members match this view.</td>
                          </tr>
                        ) : (
                          pagedMembers.map((m) => (
                            <tr key={m.id}>
                              <td style={td}>{m.email || "—"}</td>

                              <td style={td}>{m.status || "—"}</td>
                              <td style={td}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button
                                    onClick={() =>
                                      setMemberStatus(
                                        m.id,
                                        m.status === "suspended" ? "reactivate" : "suspend"
                                      )
                                    }
                                    disabled={memberBusyId === m.id}
                                    style={{
                                      height: 34,
                                      padding: "0 10px",
                                      borderRadius: 6,
                                      border: "1px solid #2a0516",
                                      background: m.status === "suspended" ? "#fff" : "#2a0516",
                                      color: m.status === "suspended" ? "#2a0516" : "#fff",
                                      fontWeight: 600,
                                      cursor: "pointer"
                                    }}
                                  >
                                    {memberBusyId === m.id ? "Saving..." : m.status === "suspended" ? "Reactivate" : "Suspend"}
                                  </button>
                                  <button
                                    onClick={() => removeMember(m.id)}
                                    disabled={memberBusyId === m.id}
                                    style={{
                                      height: 34, padding: "0 10px", borderRadius: 6,
                                      border: "1px solid #b91c1c", background: "#fff",
                                      color: "#b91c1c", fontWeight: 600, cursor: "pointer"
                                    }}
                                  >
                                    Remove
                                  </button>
                                  <button
                                    onClick={() => navigate(`/tenant/${tenantId}/member/${m.id}`)}
                                    style={{
                                      height: 34, padding: "0 10px", borderRadius: 6,
                                      border: "none", background: "#003353",
                                      color: "#fff", fontWeight: 600, cursor: "pointer"
                                    }}
                                  >
                                    Manage
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
                    <div style={{ color: "#657899", fontSize: 14 }}>
                      Showing {pagedMembers.length} of {filteredMembers.length}
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => setMemberPage((p) => Math.max(1, p - 1))}
                        disabled={safeMemberPage <= 1}
                        style={{
                          height: 34,
                          padding: "0 10px",
                          borderRadius: 6,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          color: "#2a0516",
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Prev
                      </button>

                      <span style={{ color: "#657899", fontSize: 14 }}>
                        Page {safeMemberPage} of {memberPageCount}
                      </span>

                      <button
                        type="button"
                        onClick={() => setMemberPage((p) => Math.min(memberPageCount, p + 1))}
                        disabled={safeMemberPage >= memberPageCount}
                        style={{
                          height: 34,
                          padding: "0 10px",
                          borderRadius: 6,
                          border: "1px solid #d6dbe7",
                          background: "#fff",
                          color: "#2a0516",
                          fontWeight: 600,
                          cursor: "pointer"
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

const th = {
  textAlign: "left",
  padding: "12px 14px",
  borderBottom: "1px solid #e8ebf3",
  color: "#657899",
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: "nowrap"
};

const td = {
  padding: "14px",
  borderBottom: "1px solid #f0f2f7",
  verticalAlign: "top"
};